import React from 'react';
import { useNavigate } from 'react-router-dom';
import ParticleBackground from '../components/ParticleBackground';
import { cn } from '../lib/utils';

// Import ShadCN components
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";

export function MagicLinkInfoPage() {
  const navigate = useNavigate();

  const getCurrentTime = () => {
    const melbourneTime = new Date().toLocaleString('en-US', { timeZone: 'Australia/Melbourne' });
    const hour = new Date(melbourneTime).getHours();
    if (hour < 12) return 'Good Morning';
    else if (hour < 17) return 'Good Afternoon';
    else return 'Good Evening';
  };

  return (
    <div className="flex justify-center items-center min-h-screen relative bg-transparent" data-component="MagicLinkInfoPage">
      <ParticleBackground />

      {/* Card with improved mobile responsiveness and proper centering */}
      <div className="absolute inset-0 flex items-center justify-center p-2">
        <Card className="relative z-10 w-full max-w-[95%] xs:max-w-[90%] sm:max-w-lg bg-white/95 backdrop-blur-md rounded-[16px] shadow-lg animate-slideUp px-3 xs:px-4 sm:px-5 py-3 xs:py-4 sm:py-5">
          <CardHeader className="space-y-1 text-center p-0 pb-2">
            <div className="flex justify-center">
              <img
                src="/aigp-logo.png"
                alt="AIGP Health"
                className="h-12 xs:h-14 sm:h-16 w-auto mb-1 transition-transform hover:scale-[1.02]"
              />
            </div>
            <h3 className="text-[#356BAD] text-[0.85rem] xs:text-[0.9rem] font-medium opacity-90 tracking-[-0.2px]">
              {getCurrentTime()}
            </h3>
            <h2 className="text-[1rem] xs:text-[1.1rem] sm:text-[1.25rem] font-semibold bg-gradient-to-r from-[#1E355C] to-[#356BAD] bg-clip-text text-transparent leading-tight tracking-[-0.3px]">
              Welcome to Doc Assist Patient Portal
            </h2>
          </CardHeader>

          <CardContent className="space-y-3 xs:space-y-4 p-0">
            <Alert className="border-[#356BAD] bg-[#f0f9ff] py-2">
              <AlertDescription className="text-[#1E355C] text-center text-[0.8rem] xs:text-[0.85rem]">
                <div className="flex items-center justify-center mb-1">
                  <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <strong>Magic Link Required</strong>
                </div>
                To start your consultation, you'll need a magic link from your healthcare provider.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <div className="text-center">
                <h3 className="text-[0.85rem] xs:text-[0.9rem] font-semibold text-[#2c3e50] mb-2">
                  How to Access Your Consultation:
                </h3>
                
                <div className="space-y-2 text-left">
                  <div className="flex items-start space-x-2.5">
                    <div className="flex-shrink-0 w-5 h-5 bg-[#356BAD] text-white rounded-full flex items-center justify-center text-xs font-semibold">
                      1
                    </div>
                    <p className="text-[0.75rem] xs:text-[0.8rem] text-[#64748b] leading-snug">
                      Your healthcare provider will send you a magic link via SMS or email
                    </p>
                  </div>
                  
                  <div className="flex items-start space-x-2.5">
                    <div className="flex-shrink-0 w-5 h-5 bg-[#356BAD] text-white rounded-full flex items-center justify-center text-xs font-semibold">
                      2
                    </div>
                    <p className="text-[0.75rem] xs:text-[0.8rem] text-[#64748b] leading-snug">
                      Click the magic link to access your consultation session
                    </p>
                  </div>
                  
                  <div className="flex items-start space-x-2.5">
                    <div className="flex-shrink-0 w-5 h-5 bg-[#356BAD] text-white rounded-full flex items-center justify-center text-xs font-semibold">
                      3
                    </div>
                    <p className="text-[0.75rem] xs:text-[0.8rem] text-[#64748b] leading-snug">
                      Enter the verification code sent to your registered phone number
                    </p>
                  </div>
                  
                  <div className="flex items-start space-x-2.5">
                    <div className="flex-shrink-0 w-5 h-5 bg-[#356BAD] text-white rounded-full flex items-center justify-center text-xs font-semibold">
                      4
                    </div>
                    <p className="text-[0.75rem] xs:text-[0.8rem] text-[#64748b] leading-snug">
                      Start your consultation with your healthcare provider
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Alert className="border-[#fbbf24] bg-[#fffbeb] py-2">
                <AlertDescription className="text-[#92400e] text-center text-[0.75rem] xs:text-[0.8rem]">
                  <strong>Don't have a magic link?</strong><br />
                  Contact your healthcare provider to schedule a consultation.
                </AlertDescription>
              </Alert>

              <div className="text-center">
                <p className="text-[0.7rem] xs:text-[0.75rem] text-[#9ca3af] leading-tight">
                  For technical support or questions, contact your healthcare provider's support team.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 